'use client';

/**
 * WebBlogIndexCard — Resources page blog index.
 *
 * Ports klyde-website/src/pages/Resources.tsx articles list. Featured
 * zone (large card with primary-tinted border) renders first, derived
 * from items with `featured=true`. Grid below lists every article
 * (featured ones surface twice — same as source).
 *
 * Each article is a hero-card surface — eyebrow + title + summary +
 * slug. The link points at `/blog/<slug>`. Long-form bodies live
 * separately as Media blobs (deferred — embeddings + GCS once the
 * agent retrieval path is decided).
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain canvas
 */

import { useEffect, useState } from 'react';
import { Loader2, XCircle, ArrowRight } from 'lucide-react';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { api } from '@/lib/api/client';
import { useCardTypography } from '@/lib/design/card-chrome';
import { cn } from '@/lib/utils/cn';
import type { WebBlogIndexContent } from '@/lib/api/types';
import { useLanguageStore } from '@/lib/stores/language-store';


interface WebBlogIndexCardProps {
  strategyAction?: string;
}

export function WebBlogIndexCard({
  strategyAction = 'web_resources_blog_index',
}: WebBlogIndexCardProps = {}) {
  const T = useCardTypography();
  const strategy = useCapabilityStore(
    (s) => s.strategyName('content', strategyAction),
  );
  const lang = useLanguageStore((s) => s.lang) ?? undefined;

  const [data, setData] = useState<WebBlogIndexContent | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!strategy) return;
    let cancelled = false;
    api
      .getStrategyData<WebBlogIndexContent>(strategy, lang ? { lang } : {})
      .then((res) => {
        if (cancelled) return;
        if (!res.success) {
          setError(res.error || 'Failed to load articles');
          return;
        }
        setData(res.data ?? null);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message ?? 'Network error');
      });
    return () => { cancelled = true; };
  }, [strategy, lang]);

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-destructive">
        <XCircle className="h-6 w-6 mb-2" />
        <p className={T.body}>{error}</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex items-center justify-center py-24 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin mr-2" />
        Loading…
      </div>
    );
  }

  const featured = data.items.filter((a) => a.featured);

  return (
    <section className="web-surface relative pb-20 px-6 bg-background text-foreground overflow-hidden">
      <div className="container mx-auto">
        {featured.map((article) => (
          <a
            key={`featured-${article.slug}`}
            href={`/blog/${article.slug}`}
            className="block mb-16 group"
          >
            <div className="rounded-xl border border-primary/30 bg-card p-8 md:p-10 transition-all hover:border-primary/60 hover:shadow-lg hover:shadow-primary/5">
              <span className={cn(T.header, 'mb-4 block text-primary')}>
                {article.eyebrow ?? 'Featured'}
              </span>
              <h2 className={cn(T.sectionLabel, 'mb-3 text-2xl md:text-3xl group-hover:text-primary transition-colors')}>
                {article.title}
              </h2>
              <p className={cn(T.body, 'max-w-2xl mb-5')}>
                {article.summary}
              </p>
              <span className="inline-flex items-center gap-1 text-sm font-medium text-primary">
                Read Article
                <ArrowRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
              </span>
            </div>
          </a>
        ))}

        <h3 className={cn(T.sectionLabel, 'mb-6 text-xl')}>
          {data.section_label}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {data.items.map((article) => (
            <a
              key={article.slug}
              href={`/blog/${article.slug}`}
              className="group block rounded-xl border border-border bg-card p-6 transition-all hover:border-primary/40 hover:shadow-md"
            >
              <h4 className={cn(T.sectionLabel, 'mb-2 text-lg group-hover:text-primary transition-colors')}>
                {article.title}
              </h4>
              <p className={cn(T.body, 'mb-4 text-sm line-clamp-2')}>
                {article.summary}
              </p>
              <span className="inline-flex items-center gap-1 text-sm font-medium text-primary">
                Read
                <ArrowRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
              </span>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}
